import posthog from 'src/utils/pixels/posthog'

/**
 * Vercel BotID pre-flight verification for lead forms.
 *
 * Architecture: SpotMe lead forms submit via Apollo directly to
 * spotme-server (cross-origin), which bypasses Next.js entirely. BotID's
 * `checkBotId()` can only run inside Next.js API routes, so we add a
 * tiny pre-flight: each form POSTs to `/api/botid/verify` BEFORE its
 * Apollo mutation, gets back `{ isBot }`, and either logs the signal
 * (soft-flag, current policy) or short-circuits the submit (hard-block,
 * not yet enabled).
 *
 * Soft-flag policy:
 *   - The lead submission ALWAYS proceeds, even when `isBot: true`.
 *   - The signal is logged to PostHog (event `botid_check`) and the
 *     caller decides whether to also log to Datadog.
 *   - Sales triages flagged contacts in HubSpot. This avoids ever
 *     losing a real B2B lead to a false positive.
 *
 * Failure modes (all return `{ isBot: false, checkFailed: true }`):
 *   - Network timeout (>5s)
 *   - /api/botid/verify returns non-200
 *   - BotID server check throws (e.g. Vercel edge hiccup)
 *
 * See spotme-client/docs/marketing/botid.md for the full pipeline and
 * the PostHog insight that surfaces flag rates.
 */

type VerifyBotIdContext = {
  /**
   * Identifies which form surface triggered the check, snake_case. Used
   * for PostHog breakdowns. Known values today (extend freely):
   *
   *   - `leadform_demand_monthly`     (listing LeadForm, Monthly deal)
   *   - `leadform_demand_broker`      (listing LeadForm, Broker deal)
   *   - `leadform_demand_traditional` (listing LeadForm or /tradicional)
   *   - `leadform_demand_fleet`       (fleet pill on parking listings)
   *   - `leadform_supply_*`           (host-side LeadForm — /tradicional, etc.)
   *   - `kyc_verification`            (useSubmitUser — final KYC submit; the
   *                                    most abuse-attractive surface: bots
   *                                    burning OTP + creating verified accounts)
   *   - `b2b_hero`                    (B2BHeroForm — /anfitrion/negocio hero)
   *   - `b2b_contact`                 (B2BContact — /anfitrion/negocio inline contact section)
   *   - `bodegas_hero`                (BodegasHeroForm — /anfitrion/bodegas)
   *   - `estacionamiento_hero`        (EstacionamientoHeroForm — /anfitrion/estacionamiento)
   *   - `traditional_hero`            (TraditionalHeroForm — /anfitrion/tradicional)
   *   - `valua_calculator`            (Calculate/Form — /anfitrion/valua-tu-propiedad)
   *   - `ondemand_wizard`             (OnDemandWizard — /almacenamiento-a-domicilio,
   *                                    covers regular submit + WA escape-hatch)
   *   - `personalized_specialist`     (SpecialistSection — /landing/personalized
   *                                    specialist contact form)
   *   - `preauth_reservar`            (PreAuthReservar — /reservar pre-OTP lead
   *                                    capture, fires alongside Firebase OTP send.
   *                                    HIGH-VALUE: Firebase OTP costs per send,
   *                                    a bot can enumerate phone numbers here)
   *   - `supply_individual`           (useHandleSubmitLocation — every "Subir
   *                                    tu espacio" CTA across /anfitrion/* +
   *                                    the global NavBar button. Fires alongside
   *                                    the supplyLead({funnel:'individual'}))
   */
  surface: string
}

export type VerifyBotIdResult = {
  isBot: boolean
  /**
   * True when the BotID check itself failed (timeout, network, server
   * error). Soft-flag mode treats this identically to `isBot: false` —
   * the submission proceeds. Surfaced so callers can log it separately
   * if they want a distinct Datadog signal.
   */
  checkFailed: boolean
}

// Empirical (2026-05-19 → 2026-06-16 PostHog window): 100% of
// `check_failed` events were AbortController timeouts (zero HTTP errors,
// zero network errors). On `kyc_verification` 40% of submissions timed
// out — BotID's Kasada round-trip is just slow enough to lose on mobile
// networks. 8s gives the preflight more headroom; the user has already
// clicked submit and the spinner is up, so the extra 3s is invisible
// UX-wise but should cut the silent fail-open rate roughly in half.
const TIMEOUT_MS = 8000

export async function verifyBotId(
  context: VerifyBotIdContext
): Promise<VerifyBotIdResult> {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS)

  try {
    const res = await fetch('/api/botid/verify', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: '{}',
      signal: controller.signal
    })

    if (!res.ok) {
      posthog.event('botid_check', {
        isBot: false,
        check_failed: true,
        failure_reason: `http_${res.status}`,
        surface: context.surface
      })
      return { isBot: false, checkFailed: true }
    }

    const data = (await res.json()) as { isBot?: boolean }
    const isBot = data.isBot === true

    posthog.event('botid_check', {
      isBot,
      check_failed: false,
      surface: context.surface
    })

    return { isBot, checkFailed: false }
  } catch (error: unknown) {
    const isAbort = error instanceof Error && error.name === 'AbortError'
    posthog.event('botid_check', {
      isBot: false,
      check_failed: true,
      failure_reason: isAbort ? 'timeout' : 'network',
      surface: context.surface
    })
    return { isBot: false, checkFailed: true }
  } finally {
    clearTimeout(timer)
  }
}
